import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useGameStore } from '@/hooks/useGameState';
import { useHighScore } from '@/hooks/useHighScore';
import { LanguageSwitcher } from './LanguageSwitcher';

type GameMode = 'single' | 'multi';

interface MainMenuProps {
  onStartGame: (name: string, mode: GameMode) => void;
}

export const MainMenu = ({ onStartGame }: MainMenuProps) => {
  const { t } = useTranslation();
  const [callsign, setCallsign] = useState('');
  const [gameMode, setGameMode] = useState<GameMode | null>(null);
  const isMuted = useGameStore((state) => state.isMuted);
  const setMuted = useGameStore((state) => state.setMuted);
  const quality = useGameStore((state) => state.quality);
  const setQuality = useGameStore((state) => state.setQuality);
  const { getTopScores } = useHighScore();
  const topScores = getTopScores(5);

  // Sanitize and validate player name
  const sanitizeName = (name: string): string => {
    // Remove special characters except spaces, letters, numbers
    return name.replace(/[^a-zA-Z0-9\s]/g, '').trim();
  };

  const handleLaunch = () => {
    if (!gameMode) return;
    let name: string;
    if (gameMode === 'multi') {
      const sanitized = sanitizeName(callsign);
      // Use default if empty or too short, max 15 chars
      name = sanitized.length >= 2 ? sanitized.slice(0, 15) : t('mainMenu.defaultNameMulti');
    } else {
      name = t('mainMenu.defaultNameSingle');
    }
    onStartGame(name, gameMode);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && gameMode) {
      handleLaunch();
    }
  };

  const handleModeSelect = (mode: GameMode) => {
    setGameMode(mode);
  };

  const handleBack = () => {
    setGameMode(null);
    setCallsign('');
  };

  return (
    <div className="absolute inset-0 flex items-center justify-center z-50 bg-black/90 backdrop-blur-md overflow-auto">
      <div className="flex flex-col lg:flex-col items-center gap-3 sm:gap-6 lg:gap-8 p-4 sm:p-6 lg:p-8 relative w-full max-w-4xl">

        <div className="flex flex-row lg:flex-col items-center gap-4 lg:gap-8 w-full">
          {/* Left side: Logo + Tagline */}
          <div className="flex flex-col items-center flex-1 min-w-0">
            <div className="text-center">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-primary neon-text animate-pulse-glow tracking-wider whitespace-nowrap">
                {t('mainMenu.title')}
              </h1>
              <p className="text-sm sm:text-lg md:text-xl text-secondary neon-text-magenta mt-1 tracking-widest">
                {t('mainMenu.titleSuffix')}
              </p>
            </div>

            <p className="text-muted-foreground text-xs sm:text-sm md:text-lg tracking-wide mt-2 text-center">
              {t('mainMenu.tagline')}
            </p>
          </div>

          {/* Right side: Mode Selection or Callsign Input */}
          <div className="flex flex-col gap-2 sm:gap-4 w-full max-w-[200px] sm:max-w-sm flex-1">
            {gameMode === null ? (
              /* Mode Selection */
              <>
                <button
                  onClick={() => handleModeSelect('single')}
                  className="neon-button text-sm sm:text-xl py-2 sm:py-3"
                >
                  {t('mainMenu.singlePlayer')}
                </button>
                <div className="relative">
                  <button
                    disabled
                    className="neon-button-secondary text-sm sm:text-xl py-2 sm:py-3 opacity-50 cursor-not-allowed w-full"
                  >
                    {t('mainMenu.multiplayer')}
                  </button>
                  <span className="absolute -top-2 -right-2 bg-secondary text-secondary-foreground text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full font-bold tracking-wide animate-pulse">
                    {t('mainMenu.soon')}
                  </span>
                </div>
              </>
            ) : gameMode === 'multi' ? (
              /* Multiplayer: Callsign Input */
              <>
                <button
                  onClick={handleBack}
                  className="text-muted-foreground text-xs sm:text-sm hover:text-primary transition-colors self-start"
                >
                  {t('mainMenu.back')}
                </button>
                <input
                  type="text"
                  placeholder={t('mainMenu.callsignPlaceholder')}
                  value={callsign}
                  onChange={(e) => setCallsign(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="neon-input text-center text-sm sm:text-lg tracking-wider py-2 sm:py-3"
                  maxLength={15}
                  minLength={2}
                  autoFocus
                />
                <button
                  onClick={handleLaunch}
                  className="neon-button text-sm sm:text-xl py-2 sm:py-3"
                >
                  {t('mainMenu.launch')}
                </button>
              </>
            ) : (
              /* Single Player: Direct Launch */
              <>
                <button
                  onClick={handleBack}
                  className="text-muted-foreground text-xs sm:text-sm hover:text-primary transition-colors self-start"
                >
                  {t('mainMenu.back')}
                </button>
                <button
                  onClick={handleLaunch}
                  className="neon-button text-sm sm:text-xl py-2 sm:py-3"
                >
                  {t('mainMenu.launchSingle')}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="text-muted-foreground text-xs sm:text-sm text-center max-w-md space-y-1 sm:space-y-2 hidden sm:block">
          <p>{t('mainMenu.instructionsMouse')}</p>
          <p>{t('mainMenu.instructionsMobile')}</p>
          <p>{t('mainMenu.instructionsCollect')}</p>
        </div>

        <div className="text-muted-foreground text-xs text-center sm:hidden">
          <p>{t('mainMenu.instructionsMobile')}</p>
        </div>

        {/* Mute Toggle */}
        <button
          onClick={() => setMuted(!isMuted)}
          className="absolute top-2 left-2 sm:top-4 sm:left-4 text-xl sm:text-3xl hover:scale-110 transition-transform"
          title={isMuted ? t('mainMenu.unmute') : t('mainMenu.mute')}
        >
          {isMuted ? '🔇' : '🔊'}
        </button>

        {/* Quality Toggle */}
        <button
          onClick={() => setQuality(quality === 'high' ? 'low' : 'high')}
          className="absolute top-2 left-10 sm:top-4 sm:left-16 text-xl sm:text-3xl hover:scale-110 transition-transform"
          title={quality === 'high' ? t('mainMenu.qualityLow') : t('mainMenu.qualityHigh')}
        >
          {quality === 'high' ? '🖥️' : '📱'}
        </button>

        {/* Language Switcher */}
        <div className="absolute top-2 left-[4.5rem] sm:top-4 sm:left-28">
          <LanguageSwitcher />
        </div>

        {/* Leaderboard - Two columns */}
        <div className="mt-2 sm:mt-4 hidden md:flex gap-4 w-full max-w-lg">
          {/* Single Player Scores */}
          <div className="hud-panel flex-1">
            <h3 className="text-primary text-xs sm:text-sm font-bold mb-2 tracking-wider">{t('mainMenu.leaderboardSingle')}</h3>
            <div className="space-y-1 text-xs text-muted-foreground">
              {topScores.length > 0 ? (
                topScores.map((entry, index) => (
                  <p key={entry.timestamp}>
                    {index + 1}. {entry.playerName} - {entry.score}
                  </p>
                ))
              ) : (
                <p className="text-muted-foreground/50 italic">{t('mainMenu.noScores')}</p>
              )}
            </div>
          </div>

          {/* Multiplayer Scores - Coming Soon */}
          <div className="hud-panel flex-1 relative opacity-60">
            <h3 className="text-secondary text-xs sm:text-sm font-bold mb-2 tracking-wider">{t('mainMenu.leaderboardMulti')}</h3>
            <div className="space-y-1 text-xs text-muted-foreground">
              <p className="text-muted-foreground/50 italic">{t('mainMenu.comingSoon')}</p>
            </div>
            <span className="absolute -top-2 -right-2 bg-secondary text-secondary-foreground text-[10px] sm:text-xs px-1.5 py-0.5 rounded-full font-bold tracking-wide animate-pulse">
              {t('mainMenu.soon')}
            </span>
          </div>
        </div>

        {/* Legal Footer */}
        <div className="mt-6 sm:mt-8 text-center w-full text-muted-foreground/50 text-[10px] sm:text-xs pb-4">
          <Link to="/privacy" className="hover:text-muted-foreground transition-colors">Privacy Policy</Link>
          {' · '}
          <Link to="/terms" className="hover:text-muted-foreground transition-colors">Terms</Link>
        </div>
      </div>
    </div>
  );
};
